import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useToast } from '../../components/Toast';
import Icon, { type IconName } from '../../components/Icon';
import { Banner, PageLoading, Select } from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';
import type { TeamMember, VisitDashboard, DashboardTrendPoint } from '../../types';

/**
 * The visit dashboard — the management overview in one screen.
 *
 * Assigned vs completed, how far off the pin people mark, per-employee
 * performance, average time on a call, and total time on the move. Day,
 * month or a custom range, and filterable to one person. Managers see
 * their own team; admins see everyone — the scope is the server's.
 */

function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function firstOfMonth(): string {
  const d = new Date();
  return iso(new Date(d.getFullYear(), d.getMonth(), 1));
}
function today(): string {
  return iso(new Date());
}

/** Seconds → "3h 12m" / "48m" / "—". */
function hoursMins(seconds: number | null): string {
  if (seconds == null) return '—';
  const m = Math.round(seconds / 60);
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h && r) return `${h}h ${r}m`;
  if (h) return `${h}h`;
  return `${r}m`;
}

/** Minutes → "42m" / "1h 5m". */
function mins(m: number | null): string {
  if (m == null) return '—';
  return hoursMins(Math.round(m * 60));
}

type Preset = 'today' | 'month' | 'custom';

export default function VisitDashboardPage() {
  const toast = useToast();
  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(today);
  const [userId, setUserId] = useState('');
  const [preset, setPreset] = useState<Preset>('month');

  const [data, setData] = useState<VisitDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [salesmen, setSalesmen] = useState<TeamMember[]>([]);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<VisitDashboard>('/reports/visit-dashboard', {
        from, to, userId: userId || undefined,
      });
      setData(res);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load the dashboard.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, userId]);

  useEffect(() => { void run(); /* first load */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get<TeamMember[]>('/users')
      .then((rows) => setSalesmen(rows.filter((u) => u.role === 'sales_rep' || u.role === 'manager')))
      .catch(() => { /* the filter is optional */ });
  }, []);

  const applyPreset = (p: Preset) => {
    setPreset(p);
    if (p === 'today') { setFrom(today()); setTo(today()); }
    else if (p === 'month') { setFrom(firstOfMonth()); setTo(today()); }
  };

  const s = data?.summary;

  return (
    <>
      <PageHeader
        title="Visit dashboard"
        subtitle="Assigned vs done, distance, timings and travel — for the range and person you pick."
        actions={(
          <button type="button" className="btn btn-primary" onClick={() => void run()}>
            <Icon name="history" size={15} /> Refresh
          </button>
        )}
      />

      <div className="page col gap-16">
        {/* -- filters ------------------------------------------------- */}
        <div className="card" style={{ padding: 14 }}>
          <div className="row gap-12" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="tabs" style={{ flex: 'none' }}>
              {(['today', 'month', 'custom'] as Preset[]).map((p) => (
                <button key={p} type="button"
                  className={`tab${preset === p ? ' active' : ''}`}
                  onClick={() => applyPreset(p)}>
                  {p === 'today' ? 'Today' : p === 'month' ? 'This month' : 'Custom'}
                </button>
              ))}
            </div>
            <div className="field" style={{ margin: 0 }}>
              <label>From</label>
              <input type="date" className="input" value={from}
                onChange={(e) => { setFrom(e.target.value); setPreset('custom'); }} />
            </div>
            <div className="field" style={{ margin: 0 }}>
              <label>To</label>
              <input type="date" className="input" value={to}
                onChange={(e) => { setTo(e.target.value); setPreset('custom'); }} />
            </div>
            <div style={{ minWidth: 180 }}>
              <Select label="Employee" value={userId}
                onChange={(e) => setUserId(e.target.value)}>
                <option value="">Everyone</option>
                {salesmen.map((u) => (
                  <option key={u.id} value={u.id}>{u.fullName}</option>
                ))}
              </Select>
            </div>
            <button type="button" className="btn btn-primary" onClick={() => void run()}>
              Apply
            </button>
          </div>
        </div>

        {loading ? <PageLoading rows={5} /> : !data || !s ? (
          <Banner kind="info">No data for this range yet.</Banner>
        ) : (
          <>
            {/* -- KPI tiles -------------------------------------------- */}
            <div className="stat-row" style={{ marginBottom: 0 }}>
              <Stat icon="checklist" label="Visits completed"
                value={s.visitsCompleted.toLocaleString('en-IN')}
                sub={data.hasPlan
                  ? `of ${(s.visitsAssigned ?? 0).toLocaleString('en-IN')} assigned`
                  : `${s.productive} productive`} />
              <Stat icon="check"
                label={data.hasPlan ? 'Completion' : 'Productive'}
                value={data.hasPlan
                  ? (s.completionPct != null ? `${s.completionPct}%` : '—')
                  : (s.visitsCompleted ? `${Math.round((s.productive / s.visitsCompleted) * 100)}%` : '—')}
                sub={data.hasPlan ? 'assigned → completed' : 'of completed visits'} />
              <Stat icon="pin" label="Avg marked distance"
                value={s.avgMarkedDistanceM != null ? `${s.avgMarkedDistanceM} m` : '—'}
                sub="from the customer's pin" />
              <Stat icon="history" label="Avg visit time"
                value={mins(s.avgVisitMinutes)}
                sub="time on site per call" />
            </div>

            <div className="stat-row" style={{ marginBottom: 0 }}>
              <Stat icon="map" label="Total travel time"
                value={hoursMins(s.totalTravelSeconds)}
                sub={data.trackingEnabled ? 'moving, across the range' : 'tracking is off'} />
              <Stat icon="map" label="Distance travelled"
                value={s.totalTravelKm != null ? `${s.totalTravelKm.toLocaleString('en-IN')} km` : '—'}
                sub={data.trackingEnabled ? 'on the move' : 'tracking is off'} />
              <Stat icon="users" label="Active employees"
                value={`${s.activeReps}`}
                sub={`of ${s.reps} in scope`} />
              <Stat icon="checklist" label="Visits recorded"
                value={s.visitsTotal.toLocaleString('en-IN')}
                sub="completed + in-progress" />
            </div>

            {(data.notes.plan || data.notes.tracking) && (
              <div className="col gap-8">
                {data.notes.plan ? <Banner kind="info">{data.notes.plan}</Banner> : null}
                {data.notes.tracking ? <Banner kind="info">{data.notes.tracking}</Banner> : null}
              </div>
            )}

            {/* -- trend ------------------------------------------------ */}
            <div className="card card-pad">
              <div className="card-head" style={{ marginBottom: 12 }}>
                <div>
                  <h3>Assigned vs completed</h3>
                  <p className="small faint" style={{ margin: 0 }}>
                    {data.hasPlan ? 'Planned calls against completed visits, day by day.'
                      : 'Completed visits, day by day.'}
                  </p>
                </div>
                <Legend hasPlan={data.hasPlan} />
              </div>
              <TrendChart trend={data.trend} hasPlan={data.hasPlan} />
            </div>

            {/* -- employee performance -------------------------------- */}
            <div className="card">
              <div className="card-head card-pad" style={{ paddingBottom: 0 }}>
                <h3>Employee performance</h3>
              </div>
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      {data.hasPlan ? <th style={{ textAlign: 'right' }}>Assigned</th> : null}
                      <th style={{ textAlign: 'right' }}>Completed</th>
                      {data.hasPlan ? <th style={{ textAlign: 'right' }}>Completion</th> : null}
                      <th style={{ textAlign: 'right' }}>Productive</th>
                      <th style={{ textAlign: 'right' }}>Avg time</th>
                      <th style={{ textAlign: 'right' }}>Avg dist.</th>
                      {data.trackingEnabled ? <th style={{ textAlign: 'right' }}>Travel time</th> : null}
                      {data.trackingEnabled ? <th style={{ textAlign: 'right' }}>Travel km</th> : null}
                      <th style={{ textAlign: 'right' }}>Days</th>
                      <th style={{ textAlign: 'right' }}>Far</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.employees.map((e) => (
                      <tr key={e.userId}>
                        <td style={{ fontWeight: 600 }}>{e.name}</td>
                        {data.hasPlan ? <td className="num">{e.assigned ?? '—'}</td> : null}
                        <td className="num">{e.completed}</td>
                        {data.hasPlan ? (
                          <td className="num">
                            {e.completionPct != null ? <CompletionBadge pct={e.completionPct} /> : '—'}
                          </td>
                        ) : null}
                        <td className="num">{e.productive}</td>
                        <td className="num">{mins(e.avgVisitMinutes)}</td>
                        <td className="num">{e.avgMarkedDistanceM != null ? `${e.avgMarkedDistanceM} m` : '—'}</td>
                        {data.trackingEnabled ? <td className="num">{hoursMins(e.travelSeconds)}</td> : null}
                        {data.trackingEnabled ? <td className="num">{e.travelKm != null ? e.travelKm : '—'}</td> : null}
                        <td className="num">{e.daysWorked}</td>
                        <td className="num">
                          {e.farVisits ? <span style={{ color: 'var(--warning)' }}>{e.farVisits}</span> : '0'}
                        </td>
                      </tr>
                    ))}
                    {data.employees.length === 0 && (
                      <tr>
                        <td colSpan={12} style={{ textAlign: 'center', color: 'var(--ink-faint)', padding: 24 }}>
                          No employees in scope for this range.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function Stat({ icon, label, value, sub }: {
  icon: IconName; label: string; value: string; sub?: string;
}) {
  return (
    <div className="stat">
      <div className="stat-icon"><Icon name={icon} size={17} /></div>
      <div style={{ minWidth: 0 }}>
        <span className="stat-value">{value}</span>
        <span className="stat-label">{label}</span>
        {sub ? <span className="stat-label" style={{ color: 'var(--ink-faint)' }}>{sub}</span> : null}
      </div>
    </div>
  );
}

function CompletionBadge({ pct }: { pct: number }) {
  const colour = pct >= 90 ? 'var(--success)' : pct >= 70 ? 'var(--warning)' : 'var(--danger)';
  return <span style={{ fontWeight: 700, color: colour }}>{pct}%</span>;
}

function Legend({ hasPlan }: { hasPlan: boolean }) {
  return (
    <div className="row gap-12" style={{ fontSize: 12.5 }}>
      {hasPlan ? (
        <span className="row gap-8" style={{ alignItems: 'center' }}>
          <span style={{ width: 11, height: 11, borderRadius: 3, background: '#e5e7eb', display: 'inline-block' }} />
          Assigned
        </span>
      ) : null}
      <span className="row gap-8" style={{ alignItems: 'center' }}>
        <span style={{ width: 11, height: 11, borderRadius: 3, background: 'var(--brand)', display: 'inline-block' }} />
        Completed
      </span>
    </div>
  );
}

/**
 * A small grouped-bar chart, drawn inline so the page pulls in no chart
 * library. Assigned is the faint back bar, completed the brand bar in
 * front — the gap between them is the miss, read at a glance.
 */
function TrendChart({ trend, hasPlan }: { trend: DashboardTrendPoint[]; hasPlan: boolean }) {
  const W = 760;
  const H = 180;
  const padL = 28;
  const padB = 22;
  const padT = 8;
  const innerW = W - padL - 8;
  const innerH = H - padT - padB;

  const max = useMemo(() => {
    const vals = trend.flatMap((p) => [p.completed, hasPlan ? (p.assigned ?? 0) : 0]);
    return Math.max(1, ...vals);
  }, [trend, hasPlan]);

  if (trend.length === 0) {
    return <p className="faint small">Nothing in this range.</p>;
  }

  const n = trend.length;
  const slot = innerW / n;
  const barW = Math.max(3, Math.min(22, slot * (hasPlan ? 0.34 : 0.5)));
  // A tick every ~7 days keeps the axis readable across a month.
  const labelEvery = Math.ceil(n / 8);

  const yFor = (v: number) => padT + innerH - (v / max) * innerH;

  return (
    <div className="table-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: W, fontVariantNumeric: 'tabular-nums' }} role="img"
        aria-label="Assigned versus completed visits by day">
        {/* y gridlines */}
        {[0, 0.5, 1].map((t) => {
          const y = padT + innerH - t * innerH;
          return (
            <g key={t}>
              <line x1={padL} y1={y} x2={W - 8} y2={y} stroke="#eef1f5" />
              <text x={0} y={y + 3.5} fontSize="10" fill="#9ca3af">{Math.round(max * t)}</text>
            </g>
          );
        })}
        {trend.map((p, i) => {
          const cx = padL + i * slot + slot / 2;
          const assigned = hasPlan ? (p.assigned ?? 0) : 0;
          const label = i % labelEvery === 0 ? p.date.slice(8) : null; // day-of-month
          return (
            <g key={p.date}>
              {hasPlan && assigned > 0 ? (
                <rect x={cx - barW} y={yFor(assigned)} width={barW}
                  height={padT + innerH - yFor(assigned)} rx={2} fill="#e5e7eb" />
              ) : null}
              {p.completed > 0 ? (
                <rect x={hasPlan ? cx : cx - barW / 2} y={yFor(p.completed)} width={barW}
                  height={padT + innerH - yFor(p.completed)} rx={2} fill="var(--brand)" />
              ) : null}
              {label ? (
                <text x={cx} y={H - 6} fontSize="10" fill="#9ca3af" textAnchor="middle">{label}</text>
              ) : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
